import PropTypes from "prop-types";
import { forwardRef, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
    Avatar,
    Badge,
    Chip,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
} from "@mui/material";

// project import
import { activeItem } from "store/reducers/menu";

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

const NavItem = ({ item, level }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const menu = useSelector((state) => state.menu);
    const { drawerOpen, openItem, unrecognizableMessageCount } = menu;

    let itemTarget = "_self";
    if (item.target) {
        itemTarget = "_blank";
    }

    let listItemProps = {
        component: forwardRef((props, ref) => (
            <Link ref={ref} {...props} to={item.url} target={itemTarget} />
        )),
    };
    if (item?.external) {
        listItemProps = { component: "a", href: item.url, target: itemTarget };
    }

    const itemHandler = (id) => {
        dispatch(activeItem({ openItem: [id] }));
    };

    const Icon = item.icon;
    const itemIcon = item.icon ? (
        <Icon style={{ fontSize: drawerOpen ? "1rem" : "1.25rem" }} />
    ) : (
        false
    );

    const isSelected = openItem.findIndex((id) => id === item.id) > -1;

    // active menu item on page load
    useEffect(() => {
        const currentIndex = document.location.pathname
            .toString()
            .split("/")
            .findIndex((id) => id === item.id);
        if (currentIndex > -1) {
            dispatch(activeItem({ openItem: [item.id] }));
        }
        // eslint-disable-next-line
    }, []);

    const textColor = "text.primary";
    const iconSelectedColor = "primary.main";

    const navItemNotification = useMemo(() => {
        if (!item || !unrecognizableMessageCount) return;
        if (
            item.id === "shelling-info" &&
            unrecognizableMessageCount.SHELLING > 0
        ) {
            return (
                <Badge
                    badgeContent={unrecognizableMessageCount.SHELLING}
                    color="primary"
                    sx={{ float: "right" }}
                ></Badge>
            );
        } else if (
            item.id === "observation-info" &&
            unrecognizableMessageCount.OBSERVATION > 0
        ) {
            return (
                <Badge
                    badgeContent={unrecognizableMessageCount.OBSERVATION}
                    color="primary"
                    sx={{ float: "right" }}
                ></Badge>
            );
        } else if (
            item.id === "damage-info" &&
            unrecognizableMessageCount.DAMAGE > 0
        ) {
            return (
                <Badge
                    badgeContent={unrecognizableMessageCount.DAMAGE}
                    color="primary"
                    sx={{ float: "right" }}
                ></Badge>
            );
        }
        return null;
    }, [item, unrecognizableMessageCount]);

    return (
        <ListItemButton
            {...listItemProps}
            disabled={item.disabled}
            onClick={() => itemHandler(item.id)}
            selected={isSelected}
            sx={{
                zIndex: 1201,
                pl: drawerOpen ? `${level * 28}px` : 1.5,
                py: !drawerOpen && level === 1 ? 1.25 : 1,
                ...(drawerOpen && {
                    "&:hover": {
                        color: iconSelectedColor,
                        bgcolor: "primary.lighter",
                        "& .MuiListItemText-root h6": {
                            color: iconSelectedColor,
                        },
                    },
                    "&.Mui-selected": {
                        bgcolor: "primary.lighter",
                        borderRight: `2px solid ${theme.palette.primary.main}`,
                        color: iconSelectedColor,
                        "&:hover": {
                            color: iconSelectedColor,
                            bgcolor: "primary.lighter",
                        },
                    },
                }),
                ...(!drawerOpen && {
                    "&:hover": {
                        bgcolor: "transparent",
                    },
                    "&.Mui-selected": {
                        "&:hover": {
                            bgcolor: "transparent",
                        },
                        bgcolor: "transparent",
                    },
                }),
            }}
        >
            {itemIcon && (
                <ListItemIcon
                    sx={{
                        minWidth: 28,
                        color: iconSelectedColor,
                        ...(!drawerOpen && {
                            borderRadius: 1.5,
                            width: 36,
                            height: 36,
                            alignItems: "center",
                            justifyContent: "center",
                        }),
                        ...(!drawerOpen &&
                            isSelected && {
                                bgcolor: "primary.lighter",
                            }),
                    }}
                >
                    {itemIcon}
                </ListItemIcon>
            )}
            {(drawerOpen || (!drawerOpen && level !== 1)) && (
                <ListItemText
                    primary={
                        <Typography
                            style={{ width: "80%", whiteSpace: "normal" }}
                            variant="h6"
                            sx={{
                                color: isSelected
                                    ? iconSelectedColor
                                    : "#d8d8d8",
                                "&:hover": {
                                    color: "primary.main",
                                },
                            }}
                        >
                            {item.title}
                            {navItemNotification}
                        </Typography>
                    }
                />
            )}
            {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
                <Chip
                    color={"#fff"}
                    variant={item.chip.variant}
                    size={item.chip.size}
                    label={item.chip.label}
                    avatar={
                        item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>
                    }
                />
            )}
        </ListItemButton>
    );
};

NavItem.propTypes = {
    item: PropTypes.object,
    level: PropTypes.number,
};

export default NavItem;
